/* eslint-disable sonarjs/cognitive-complexity, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import { HTMLAttributes, ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Button, Heading, Icon } from '@alto-avios/alto-ui'
import styles from './Modal.module.scss'

export type ModalProps = HTMLAttributes<HTMLDialogElement> & {
  isOpen: boolean
  onClose: () => void
  title?: string
  preventScroll?: boolean
  shouldFocusAfterRender?: boolean
  isCentered?: boolean
  titleTextAlign?: 'left' | 'center' | 'right'
  size?: 'sm' | 'md' | 'xl' | 'full'
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

function Modal(props: ModalProps): ReactNode {
  const {
    isOpen,
    onClose,
    children,
    preventScroll,
    shouldFocusAfterRender,
    title,
    isCentered = true,
    titleTextAlign = 'center',
    className,
    size = 'xl',
    headingTag = 'h2',
    ...other
  } = props

  const modalRoot = typeof window !== 'undefined' ? document.getElementById('modal') : null

  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow ?? ''
    if (preventScroll && isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [preventScroll, isOpen])

  // Focus modal content after render if required
  useEffect(() => {
    if (shouldFocusAfterRender && isOpen) {
      const modalContent = document.getElementById('modal')
      modalContent?.focus()
    }
  }, [shouldFocusAfterRender, isOpen])

  // Close modal on pressing the Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen || !modalRoot) {
    return null
  }

  return createPortal(
    <>
      <div className={styles.overlay} onClick={onClose} />
      <dialog
        onClose={onClose}
        className={classNames(
          styles.modal,
          className,
          size ? styles[size] : undefined,
          isCentered ? styles.centered : undefined
        )}
        title={title}
        data-testid="modal"
        open={isOpen}
        {...other}
      >
        <div className={styles.container}>
          <div className={classNames(styles.header, title ? styles[titleTextAlign] : undefined)}>
            {title ? (
              <Heading as={headingTag} foregroundColor="accentSecondary">
                {title}
              </Heading>
            ) : null}
            <Button
              aria-label="Close modal"
              data-testid="modal_close_button"
              onPress={onClose}
              styleVariant="white"
            >
              <Icon iconName="xmark" iconSize="1x" />
            </Button>
          </div>
          <div className={styles.content} data-testid="modal_content">
            {children}
          </div>
        </div>
      </dialog>
    </>,
    modalRoot
  )
}

export default Modal
